import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Check, Search, RefreshCw, Info, Loader2, Plus, AlertCircle, X, Trash } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import toast from 'react-hot-toast';

/**
 * Book Topic slide for selecting book search terms to include in the app
 */
const BookTopicSlide = ({ registerValidation, organization }) => {
    const {
        bookTitles,
        setBookTitles,
        setIsLoading,
        setLoadingMessage,
        appName,
        bookSearchTerm,
        setBookSearchTerm,
        bookQueries,
        setBookQueries,
        primaryColor,
        brandDataFetched,
        contentCategories: contextContentCategories
    } = useAppDemo();

    const [searchTerm, setSearchTerm] = useState(bookSearchTerm || '');
    const [bookResults, setBookResults] = useState([]);
    const [selectedBooks, setSelectedBooks] = useState(bookTitles || []);
    const [savedQueries, setSavedQueries] = useState(bookQueries || []);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);
    const [contentCategories, setContentCategories] = useState([]);
    const [contentCategoriesLoaded, setContentCategoriesLoaded] = useState(false);
    
    // Add refs to track component state
    const effectRan = useRef(false);
    const hasFetchedCategories = useRef(false);
    const hasProcessedCategories = useRef(false);
    const initialSearchExecuted = useRef(false);
    const processingCategories = useRef(false);

    // Make sure we have the latest state from context
    useEffect(() => {
        if (bookSearchTerm) {
            setSearchTerm(bookSearchTerm);
        }
        if (bookTitles) {
            setSelectedBooks(bookTitles);
        }
        if (bookQueries) {
            setSavedQueries(bookQueries);
        }
    }, [bookSearchTerm, bookTitles, bookQueries]);

    // SIMPLIFIED process content categories effect
    useEffect(() => {
        // Don't run if already processing
        if (processingCategories.current) return;
        
        console.log('Content categories effect running with:', {
            contextContentCategories: contextContentCategories?.length || 0,
            brandDataFetched,
            hasProcessedCategories: hasProcessedCategories.current
        });
        
        // Skip if we've already processed categories
        if (hasProcessedCategories.current) return;
        
        // Only set processing flag once we start actual processing
        if (brandDataFetched && contextContentCategories && contextContentCategories.length > 0) {
            processingCategories.current = true;
            
            console.log('Processing content categories from context:', contextContentCategories.length);
            
            // Create a fresh array of category names
            const categoryNames = contextContentCategories
                .filter(cat => cat.isChecked !== false) // Only include categories that are checked or don't have isChecked property
                .map(cat => typeof cat === 'string' ? cat : cat.name);
            
            console.log('Filtered category names:', categoryNames);
            
            // Start with a fresh array of queries
            let updatedQueries = [];
            
            // Add organization name to the beginning if valid
            if (organization?.name && 
                !organization.name.toLowerCase().includes('your') && 
                !organization.name.toLowerCase().includes('organization')) {
                updatedQueries.push(organization.name);
                console.log(`Added organization name "${organization.name}" to search queries`);
            }
            
            // Add all category names (no duplicates)
            categoryNames.forEach(name => {
                if (!updatedQueries.includes(name)) {
                    updatedQueries.push(name);
                }
            });
            
            // Make sure we have at least one query
            if (updatedQueries.length === 0) {
                updatedQueries = ['religious books'];
            }
            
            console.log('Final search queries:', updatedQueries);
            
            // Update the state immediately
            setSavedQueries(updatedQueries);
            setBookQueries(updatedQueries);
            
            // Set search term if empty
            if (!searchTerm) {
                setSearchTerm(updatedQueries[0]);
                setBookSearchTerm(updatedQueries[0]);
            }
            
            // Signal that processing is complete
            setContentCategoriesLoaded(true);
            hasProcessedCategories.current = true;
            processingCategories.current = false;
            
            // Provide feedback
            toast.success('Search terms populated from content categories');
        } else if (!hasFetchedCategories.current) {
            // Try to fetch from API
            fetchContentCategories();
        }
    }, [contextContentCategories, brandDataFetched, organization?.name, searchTerm]);

    // Separate function to fetch content categories from API
    const fetchContentCategories = async () => {
        try {
            hasFetchedCategories.current = true;
            processingCategories.current = true;
            
            // Get domain from organization
            const domain = organization?.website_url || organization?.website || organization?.domain || '';
            
            if (!domain) {
                console.log('No domain available to fetch content categories');
                initializeDefaultSearch();
                return;
            }
            
            // Normalize domain
            let normalizedDomain = domain.toLowerCase();
            try {
                if (normalizedDomain.startsWith('http')) {
                    const url = new URL(normalizedDomain);
                    normalizedDomain = url.hostname;
                }
                normalizedDomain = normalizedDomain.replace(/^www\./, '');
            } catch (e) {
                console.error('Error parsing URL:', e);
                normalizedDomain = normalizedDomain.replace(/^https?:\/\//, '').split('/')[0];
                normalizedDomain = normalizedDomain.replace(/^www\./, '');
            }
            
            console.log(`Fetching content categories for: ${normalizedDomain}`);
            const response = await fetch(`/api/website-data/domain/${normalizedDomain}/content-categories`);
            
            if (response.ok) {
                const data = await response.json();
                
                if (data && Array.isArray(data) && data.length > 0) {
                    // Process fetched categories immediately
                    setContentCategories(data);
                    
                    // Get category names and create search queries
                    const categoryNames = data.map(category => category.name);
                    
                    // Start with a fresh array of queries
                    let updatedQueries = [];
                    
                    // Add organization name to the beginning if valid
                    if (organization?.name && 
                        !organization.name.toLowerCase().includes('your') && 
                        !organization.name.toLowerCase().includes('organization')) {
                        updatedQueries.push(organization.name);
                    }
                    
                    // Add all category names (no duplicates)
                    categoryNames.forEach(name => {
                        if (!updatedQueries.includes(name)) {
                            updatedQueries.push(name);
                        }
                    });
                    
                    // Make sure we have at least one query
                    if (updatedQueries.length === 0) {
                        updatedQueries = ['religious books'];
                    }
                    
                    console.log('Setting search queries from API fetch:', updatedQueries);
                    
                    // Update the state
                    setSavedQueries(updatedQueries);
                    setBookQueries(updatedQueries);
                    
                    // Set search term if empty
                    if (!searchTerm) {
                        setSearchTerm(updatedQueries[0]);
                        setBookSearchTerm(updatedQueries[0]);
                    }
                    
                    toast.success('Search terms populated from content categories');
                } else {
                    console.log('No content categories found, using default search term');
                    initializeDefaultSearch();
                }
            } else {
                console.warn(`Failed to fetch content categories: ${response.status}`);
                initializeDefaultSearch();
            }
        } catch (error) {
            console.error('Error fetching content categories:', error);
            initializeDefaultSearch();
        } finally {
            hasProcessedCategories.current = true;
            setContentCategoriesLoaded(true);
            processingCategories.current = false;
        }
    };

    // Initialize with default search term (fallback)
    const initializeDefaultSearch = () => {
        // Only proceed if we haven't already set search terms
        if (!searchTerm && savedQueries.length === 0) {
            console.log('Initializing default search term');
            
            // Use organization name, only if it's not a generic placeholder
            let defaultTerm = '';
            if (organization?.name && 
                !organization.name.toLowerCase().includes('your') && 
                !organization.name.toLowerCase().includes('organization')) {
                defaultTerm = organization.name;
            } else if (appName) {
                // Extract first meaningful word from app name
                const appWords = appName.split(' ');
                defaultTerm = appWords[0];
                
                // Skip generic words
                if (defaultTerm.toLowerCase().includes('your') || 
                    defaultTerm.toLowerCase().includes('app') ||
                    defaultTerm.toLowerCase() === 'the') {
                    defaultTerm = appWords.length > 1 ? appWords[1] : 'religious books';
                }
            }
            
            // Fallback to a relevant default term if nothing good is found
            if (!defaultTerm || defaultTerm.length < 2 || 
                defaultTerm.toLowerCase().includes('your') || 
                defaultTerm.toLowerCase().includes('organization')) {
                defaultTerm = 'religious books';
            }
            
            console.log(`Setting initial search term to: ${defaultTerm}`);
            setSearchTerm(defaultTerm);
            setBookSearchTerm(defaultTerm);
            
            // Set the saved queries
            setSavedQueries([defaultTerm]);
            setBookQueries([defaultTerm]);
        }
        setContentCategoriesLoaded(true);
    };

    // Register validation with parent component
    useEffect(() => {
        if (registerValidation) {
            registerValidation('book-selection', savedQueries.length > 0);
        }
    }, [savedQueries, registerValidation]);

    // Simpler automatic search effect that triggers once when we have queries
    useEffect(() => {
        // Only run once and only after content categories are loaded
        if (initialSearchExecuted.current || isSearching || !contentCategoriesLoaded) {
            return;
        }
        
        console.log('Automatic search condition met:', {
            contentCategoriesLoaded,
            hasQueries: savedQueries.length > 0,
            searchTerm
        });
        
        // We need either saved queries or a search term to proceed
        if (savedQueries.length === 0 && !searchTerm) {
            console.log('No search terms available for initial search');
            return;
        }
        
        // Use a timeout to ensure state is stable
        const timer = setTimeout(() => {
            // Mark as executed first to prevent duplicates
            initialSearchExecuted.current = true;
            
            // Choose the term to search for
            const termToSearch = searchTerm || (savedQueries.length > 0 ? savedQueries[0] : null);
            
            if (termToSearch) {
                console.log(`Executing initial search for: ${termToSearch}`);
                searchBooks(termToSearch);
            }
        }, 100);
        
        return () => clearTimeout(timer);
    }, [contentCategoriesLoaded, savedQueries, searchTerm, isSearching]);

    // Search for books
    const searchBooks = async (term) => {
        if (!term) return;
        
        setIsSearching(true);
        setError(null);
        
        try {
            console.log(`Executing search for term: ${term}`);
            
            // Remove the term from the list if it already exists (to avoid duplicates)
            // and add it to the top of the list
            let updatedQueries;
            if (savedQueries.includes(term)) {
                // Don't update if already at the top
                if (savedQueries[0] === term) {
                    updatedQueries = [...savedQueries];
                } else {
                    // Remove the existing entry and add it to the top
                    updatedQueries = [term, ...savedQueries.filter(q => q !== term)];
                    setSavedQueries(updatedQueries);
                    setBookQueries(updatedQueries);
                }
            } else {
                // Add new term to top of the list
                updatedQueries = [term, ...savedQueries];
                setSavedQueries(updatedQueries);
                setBookQueries(updatedQueries);
                toast.success(`Added "${term}" to search terms`);
            }
            
            const response = await fetch(`/api/books/search?query=${encodeURIComponent(term)}`);
            const data = await response.json();
            
            if (data.success && data.books && data.books.length > 0) {
                setBookResults(data.books);
                
                // Select all books from search to ensure they're added to the app
                setSelectedBooks(data.books);
                setBookTitles(data.books);
                
                // Provide feedback to the user about the number of books selected
                toast.success(`${data.books.length} books found and selected for your app!`);
            } else {
                setBookResults([]);
                setError('No books found for this search term. Try another keyword.');
            }
        } catch (err) {
            console.error('Error searching for books:', err);
            setError(`Failed to search for books: ${err.message}`);
            setBookResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Add current search query to saved queries (puts at top of list)
    const addSearchQuery = () => {
        if (!searchTerm) return;
        
        // Don't add duplicates
        if (savedQueries.includes(searchTerm)) {
            toast.error('This search term is already saved');
            return;
        }
        
        // Add the query at the beginning of the array and update context
        const updatedQueries = [searchTerm, ...savedQueries];
        setSavedQueries(updatedQueries);
        setBookQueries(updatedQueries);
        
        toast.success(`Added "${searchTerm}" to book queries`);
    };

    // Remove a saved query
    const removeQuery = (query) => {
        const updatedQueries = savedQueries.filter(q => q !== query);
        setSavedQueries(updatedQueries);
        setBookQueries(updatedQueries);
        toast.success(`Removed "${query}" from search terms`);
    };

    // Form submit handler
    const handleSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            setBookSearchTerm(searchTerm.trim());
            searchBooks(searchTerm.trim());
        }
    };

    return (
        <div className="max-w-6xl mx-auto w-full px-4">
            <div className="flex items-center mb-4 border-b border-white/10 pb-3">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100/20 text-yellow-300 mr-3">
                    <BookOpen className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <h2 className="text-xl font-bold text-white">Book Search Terms</h2>
                    <p className="text-white/80 text-sm">Add search terms to populate your app with relevant books</p>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left column: Search and Saved Terms */}
                <div className="space-y-4">
                    {/* Search Section */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-4 hover:bg-white/20 transition-colors">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Enter search term..."
                                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-300/50"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="submit"
                                    disabled={!searchTerm.trim() || isSearching}
                                    className="px-4 py-2 bg-yellow-300 text-gray-800 rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center whitespace-nowrap"
                                >
                                    {isSearching ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Searching...
                                        </>
                                    ) : (
                                        <>
                                            <Search className="w-4 h-4 mr-2" />
                                            Search
                                        </>
                                    )}
                                </button>
                                
                                {/* Keep the explicit save button for UX clarity */}
                                <button
                                    type="button"
                                    onClick={addSearchQuery}
                                    disabled={!searchTerm.trim() || isSearching || savedQueries.includes(searchTerm)}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center whitespace-nowrap"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Save Search Term
                                </button>
                            </div>
                        </form>
                        
                        <div className="mt-3 text-sm text-white/70">
                            <p className="italic">Searches are automatically saved to your list</p>
                        </div>
                        
                        {error && (
                            <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-white">
                                <div className="flex items-start">
                                    <AlertCircle className="w-5 h-5 text-red-300 mr-2 flex-shrink-0 mt-0.5" />
                                    <p>{error}</p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Saved Search Terms Section */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-4 hover:bg-white/20 transition-colors">
                        <h3 className="text-md font-medium text-white mb-3">Saved Search Terms</h3>
                        
                        {savedQueries.length > 0 ? (
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {savedQueries.map((query, index) => (
                                    <div key={index} className="flex justify-between items-center p-3 bg-white/5 border border-white/10 rounded-lg">
                                        <div className="flex items-center">
                                            <BookOpen className="w-5 h-5 text-yellow-300 mr-2" />
                                            <span className="text-white">{query}</span>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => searchBooks(query)}
                                                className="p-2 bg-yellow-300/20 text-yellow-300 rounded-full hover:bg-yellow-300/30"
                                                title="Search this query"
                                            >
                                                <Search className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => removeQuery(query)}
                                                className="p-2 bg-red-500/20 text-red-300 rounded-full hover:bg-red-500/30"
                                                title="Remove this query"
                                            >
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/50">
                                {contentCategoriesLoaded ? (
                                    <>
                                        <p>No search terms saved yet.</p>
                                        <p className="text-sm mt-1">Search for books and save the terms you want to include in your app.</p>
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center">
                                        <Loader2 className="w-8 h-8 text-yellow-300 animate-spin mb-2" />
                                        <p>Loading content categories...</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Help info panel */}
                    <div className="p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-white">
                        <div className="flex items-start">
                            <Info className="w-5 h-5 text-blue-300 mr-2 flex-shrink-0 mt-0.5" />
                            <p className="text-sm">Save multiple search terms to create a diverse collection of books in your app. Try specific topics related to your brand.</p>
                        </div>
                    </div>
                </div>

                {/* Right column: Book Preview Results */}
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-4 hover:bg-white/20 transition-colors h-[calc(100vh-220px)] flex flex-col">
                    <h3 className="text-md font-medium text-white mb-3">Book Search Results</h3>
                    
                    <div className="flex-1 overflow-y-auto">
                        {isSearching ? (
                            <div className="flex justify-center items-center h-full">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/10 border-t-yellow-300"></div>
                            </div>
                        ) : bookResults.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {bookResults.slice(0, 12).map((book, index) => (
                                    <div 
                                        key={book.isbn13 || index}
                                        className="bg-white/5 border border-white/20 rounded-lg overflow-hidden flex flex-col h-full"
                                    >
                                        <div className="p-4 flex-1 flex flex-col">
                                            <div className="h-32 flex items-center justify-center bg-white/5 rounded mb-3">
                                                {book.image ? (
                                                    <img 
                                                        src={book.image} 
                                                        alt={book.title} 
                                                        className="h-full object-contain"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = `https://via.placeholder.com/80x120/2f3e4a/ffffff?text=${encodeURIComponent(book.title.substring(0, 10))}`;
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-20 h-24 bg-gray-500/40 flex items-center justify-center rounded">
                                                        <BookOpen className="w-8 h-8 text-white/30" />
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <h4 className="text-white font-medium leading-tight line-clamp-2 mb-1">{book.title}</h4>
                                            <p className="text-white/60 text-sm line-clamp-1">{book.authors?.join(', ') || 'Unknown Author'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-white/50 h-full flex flex-col items-center justify-center">
                                <BookOpen className="w-12 h-12 mb-3 opacity-30" />
                                <p>No books to display.</p>
                                <p className="text-sm mt-1">Search for books using terms on the left.</p>
                            </div>
                        )}
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-white">
                        <div className="flex items-start">
                            <Info className="w-5 h-5 text-blue-300 mr-2 flex-shrink-0 mt-0.5" />
                            <p className="text-sm">This is a preview. Your app will be populated with books matching all your saved search terms.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookTopicSlide; 